// Extension-explicit so scripts/generateTokensCss.mjs can import this in Node.
import { buildTokens } from './tokens.js'

/**
 * Flattens the token set into the `--custom-property` map written to `:root`.
 * The legacy names already referenced by ~46 CSS files and Tailwind are kept
 * verbatim so nothing has to be renamed during the migration.
 *
 * @param {object|null} settings entreprise settings
 * @returns {Record<string, string>}
 */
export const buildCssVariables = (settings) => {
  const t = buildTokens(settings)

  const vars = {
    // Nomad semantic brand (styles/nomad/tokens.css)
    '--primary': t.brand.primary,
    '--brand': t.brand.primary,
    '--brand-glow': t.brand.accent,
    '--primary-foreground': t.brand.onPrimary,
    '--brand-foreground': t.brand.onPrimary,
    '--sidebar-primary': t.brand.primary,
    '--sidebar-ring': t.brand.primary,
    '--gradient-brand': t.component.gradientBrand,
    '--gradient-glow': t.component.pageGlow,
    '--shadow-glow': t.component.glowShadow,
    '--page-glow': t.component.pageGlow,

    // Brand (legacy names, still consumed across the app)
    '--primary-color': t.brand.primary,
    '--primary-hover': t.brand.primaryHover,
    '--primary-active': t.brand.primaryActive,
    '--primary-soft': t.brand.primarySoft,
    '--on-primary': t.brand.onPrimary,
    '--secondary-color': t.brand.secondary,
    '--secondary-hover': t.brand.secondaryHover,
    '--secondary-soft': t.brand.secondarySoft,
    '--on-secondary': t.brand.onSecondary,
    '--accent-color': t.brand.accent,
    '--accent-hover': t.brand.accentHover,
    '--on-accent': t.brand.onAccent,

    // Component-level derived values (legacy names)
    '--table-header-bg': t.component.tableHeaderBg,
    '--table-header-color': t.component.tableHeaderColor,
    '--table-body-bg': t.component.tableBodyBg,
    '--drawer-header-bg': t.component.drawerHeaderBg,
    '--footer-bg': t.component.footerBg,

    // Surfaces and text
    '--bg-color': t.surface.page,
    '--canvas-color': t.surface.canvas,
    '--card-bg': t.surface.cardGlass,
    '--surface-card': t.surface.card,
    '--surface-sidebar': t.surface.sidebar,
    '--glass-border': t.surface.glassBorder,
    '--overlay-bg': t.surface.overlay,
    '--text-dark': t.text.dark,
    '--text-light': t.text.light,
    '--text-heading': t.text.heading,
    '--text-body': t.text.body,
    '--text-muted': t.text.muted,
    '--text-inverse': t.text.inverse,
    '--border-color': t.border.default,
    '--border-subtle': t.border.subtle,
    '--border-strong': t.border.strong,
    '--field-bg': t.field.bg,
    '--field-border': t.field.border,
    '--field-border-focus': t.field.borderFocus,
    '--field-ring': t.field.ring,

    // Semantic (legacy names preserved). The `-ink` variants are the AA-safe
    // text colour on the matching `-bg` tint; the plain name stays the fill.
    '--success-color': t.semantic.success,
    '--error-color': t.semantic.error,
    '--error-ink': t.semantic.errorInk,
    '--error-bg': t.semantic.errorBg,
    '--error-border': t.semantic.errorBorder,
    '--warning-color': t.semantic.warning,
    '--warning-ink': t.semantic.warningInk,
    '--warning-bg': t.semantic.warningBg,
    '--warning-border': t.semantic.warningBorder,
    '--warning-orange': t.semantic.warningOrange,
    '--warning-orange-bg': t.semantic.warningOrangeBg,
    '--warning-orange-border': t.semantic.warningOrangeBorder,
    '--success-green': t.semantic.success,
    '--success-green-ink': t.semantic.successInk,
    '--success-green-bg': t.semantic.successBg,
    '--success-green-border': t.semantic.successBorder,
    '--info-blue': t.semantic.info,
    '--info-blue-ink': t.semantic.infoInk,
    '--info-blue-bg': t.semantic.infoBg,
    '--info-blue-border': t.semantic.infoBorder,

    // Typography
    '--font-sans': t.font.sans,
    '--font-heading': t.font.heading,
    '--font-mono': t.font.mono,
    '--font-label': t.font.label,

    // Controls
    '--control-height': t.control.height,
    '--control-height-sm': t.control.heightSm,
    '--control-height-lg': t.control.heightLg,
    '--control-padding-x': t.control.paddingX,
    '--control-padding-y': t.control.paddingY,
    // Buttons are padded wider than fields in the reference, so they differ.
    '--button-padding-x': t.control.buttonPaddingX,
    '--button-padding-x-sm': t.control.buttonPaddingXSm,
    '--button-padding-x-lg': t.control.buttonPaddingXLg,

    // Shell and page metrics
    '--page-padding': t.layout.pagePadding,
    '--page-padding-mobile': t.layout.pagePaddingMobile,
    '--content-max-width': t.layout.contentMaxWidth,
    '--section-gap': t.layout.sectionGap,
    '--card-padding': t.layout.cardPadding,
    '--header-height': t.layout.headerHeight,
    '--sidebar-width': t.layout.sidebarWidth,
    '--sidebar-width-collapsed': t.layout.sidebarWidthCollapsed,
    '--sidebar-width-mobile': t.layout.sidebarWidthMobile,
    '--table-row-height': t.layout.tableRowHeight,

    // Motion
    '--duration-fast': t.duration.fast,
    '--duration-normal': t.duration.normal,
    '--duration-slow': t.duration.slow,
    '--easing-standard': t.easing.standard,
    '--easing-decelerate': t.easing.decelerate,
  }

  Object.entries(t.primaryRamp).forEach(([step, value]) => {
    vars[`--color-primary-${step}`] = value
  })
  Object.entries(t.accentRamp).forEach(([step, value]) => {
    vars[`--color-accent-${step}`] = value
  })
  Object.entries(t.gray).forEach(([step, value]) => {
    vars[`--gray-${step}`] = value
  })
  Object.entries(t.radius).forEach(([key, value]) => {
    vars[`--radius-${key}`] = value
  })
  Object.entries(t.shadow).forEach(([key, value]) => {
    vars[`--shadow-${key}`] = value
  })
  Object.entries(t.space).forEach(([key, value]) => {
    vars[`--space-${key}`] = value
  })
  Object.entries(t.fontSize).forEach(([key, value]) => {
    vars[`--font-size-${key}`] = value
  })
  Object.entries(t.fontWeight).forEach(([key, value]) => {
    vars[`--font-weight-${key}`] = value
  })
  Object.entries(t.lineHeight).forEach(([key, value]) => {
    vars[`--line-height-${key}`] = value
  })
  Object.entries(t.letterSpacing).forEach(([key, value]) => {
    vars[`--tracking-${key}`] = value
  })
  Object.entries(t.zIndex).forEach(([key, value]) => {
    vars[`--z-${key}`] = value
  })

  return vars
}

export const applyCssVariables = (settings) => {
  if (typeof document === 'undefined') return
  const vars = buildCssVariables(settings)
  const root = document.documentElement
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })
}

/** Reads a live CSS variable, so the kit can stay token-driven at runtime. */
export const readCssVariable = (name, fallback = '') => {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
  return value || fallback
}
